import {memoize} from "lodash"
import {getSymbolsValidation, passwordInputSymbols} from "../../ui/Input/Interfaces"
import {i18n} from "../../i18n/i18n"
import {MAX_VIDEO_DURATION_MIN, MIN_VIDEO_DURATION_SEC} from "../../constants"
import validUrl from "valid-url"

export const isRequired = (value: any) => {
    if (value === 0) return
    if (!value) return i18n.t("common:validation.required")
    if (value.trim) {
        if (!value.trim()) return i18n.t("common:validation.incorrect_value")
    }
}

export const isNumber = (value: string | undefined): string | undefined => {
    if (!value) return undefined
    if (isNaN(Number(value))) return i18n.t("common:validation.incorrect_value")
    if (!/^[0-9-]+$/.test(value)) return i18n.t("common:validation.integers_only")
}

const prohibitWebLinkSymbols = [
    ";",
    "`",
    "@",
    "#",
    "$",
    "(",
    ")",
    "*",
    "!",
    "^",
    "}",
    "{",
    "[",
    "]",
    "'",
    '"',
    "<",
    ">",
]
const warningWebLinkSymbols = ["%", "+", "-", "_", ":", "/", "\\", "?"]
export const isWebLink = (value: string | undefined): string | undefined => {
    if (!value) return undefined

    const incorrectLink = i18n.t("common:validation.incorrect_link")

    if (!isNaN(Number(value))) return incorrectLink
    let trimmedValue = (value || "").trim()
    if (trimmedValue === "/") return incorrectLink
    if (trimmedValue.endsWith("/"))
        trimmedValue = trimmedValue.substring(0, trimmedValue.length - 1)

    if (!trimmedValue) return incorrectLink
    if (prohibitWebLinkSymbols.some((char) => trimmedValue.includes(char)))
        return i18n.t("common:validation.invalid_characters")
    if (
        warningWebLinkSymbols.some(
            (char) => trimmedValue.startsWith(char) || trimmedValue.endsWith(char)
        )
    )
        return incorrectLink
}

export const isUri = (value: string) =>
    validUrl.isUri(value) ? undefined : i18n.t`common:validation.not_valid_link`

export const isNotZero = (value: string): string | undefined =>
    Number(value) ? undefined : i18n.t("common:validation.incorrect_value")

export const validateMultiSelect = (value: Array<any>): string | undefined => {
    const maxLength = 200
    if (value?.some((item) => (item?.name?.length ? item.name.length > maxLength : false)))
        return i18n.t("shared_components:tags.validation.max_symbols", {count: maxLength})
    return value?.length ? undefined : i18n.t("common:validation.required")
}

export const isNotNegative = (value: Array<any>): string | undefined =>
    Number(value) >= 0 ? undefined : i18n.t("common:validation.positive_only")

export const isMoreThanThreshold = memoize(
    (threshold: number) =>
        (value: Array<any>): string | undefined =>
            !value || Number(value) > threshold
                ? undefined
                : i18n.t("common:validation.min_value_n", {count: threshold + 1})
)

export const isLessThanThreshold = memoize(
    (threshold: number) =>
        (value: Array<any>): string | undefined =>
            !value || Number(value) < threshold
                ? undefined
                : i18n.t("common:validation.max_value_n", {count: threshold - 1})
)

export const durationValidator = (duration: number | undefined) => {
    return duration ? duration <= MAX_VIDEO_DURATION_MIN * 60 : true
}

export enum DurationValidity {
    TooShort = "LOW",
    Ok = "NORMAL",
    TooLong = "HIGH",
}

export const strictDurationValidator = (duration: number | undefined) => {
    if (duration === 0) return DurationValidity.TooShort
    if (!duration) return DurationValidity.Ok

    if (duration < MIN_VIDEO_DURATION_SEC) return DurationValidity.TooShort
    if (duration > MAX_VIDEO_DURATION_MIN * 60) return DurationValidity.TooLong
    return DurationValidity.Ok
}
export const isDurationStrictlyValid = (duration: number | undefined) =>
    strictDurationValidator(duration) === DurationValidity.Ok

export const validatePhone = memoize((length?: number) => (value?: string): string | undefined => {
    if (!value) return undefined

    const regex = /^[0-9]*$/

    if (!regex.test(value) || value.length !== length) {
        return i18n.t("common:validation.incorrect_format")
    }

    return undefined
})

const prohibitEmailSymbols = [";", "`"]
export const validateEmail: (email?: string) => string | undefined = (email) => {
    if (!email) return i18n.t("common:validation.required")
    const trimmedEmail = (email || "").trim()
    if (prohibitEmailSymbols.some((char) => trimmedEmail.includes(char)))
        return i18n.t("common:validation.invalid_characters")

    if (!/@(.+)(.+)\.(.+)/.test(trimmedEmail)) return i18n.t("common:validation.incorrect_format")
    if (
        !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
            trimmedEmail
        )
    )
        return i18n.t("common:validation.incorrect_format")
}

export const maxLengthOf = memoize((n: number) => (value?: string) => {
    if (value?.length && value.length > n)
        return i18n.t("common:validation.n_symbols_max", {count: n})
})

export const exactLengthOf = memoize((n: number) => (value?: string) => {
    if (value?.length && value.length != n)
        return i18n.t("common:validation.n_symbols_required", {count: n})
})

export const validateUppercase = (value?: string) => {
    if (value && !/[A-Z]/.test(value))
        return i18n.t("person:validation.password.need_one_uppercase")
}

export const validateLowerCase = (value?: string) => {
    if (value && !/[a-z]/.test(value))
        return i18n.t("person:validation.password.need_one_lowercase")
}

export const validateNumber = (value?: string) => {
    if (value && !/[0-9]/.test(value)) return i18n.t("person:validation.password.need_one_number")
}

export const validateSymbols = (value?: string) => {
    if (value && passwordInputSymbols.every((symbol) => !value.includes(symbol)))
        return getSymbolsValidation()
}

export const validateIP = (value?: string) => {
    const regexExp =
        /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/gi

    if (value && !regexExp.test(value)) {
        return i18n.t("common:validation.incorrect_value")
    }
}

export type FormValidator<T = Record<string, unknown>> = (
    value: string,
    formValues?: T
) => string | undefined

export const composeValidators = <T = Record<string, unknown>>(
    validators: FormValidator<T>[]
): FormValidator<T> => {
    return (value, formValues) => {
        for (const validator of validators) {
            const error = validator(value, formValues)
            if (error) return error
        }
        return undefined
    }
}

export const validateZip = (zip?: string) => {
    if (!zip?.trim()) return i18n.t("common:validation.required")
    if (zip.length > 12 || zip.trim().length < 2)
        return i18n.t("common:validation.incorrect_length")

    const regexExp = /[-\s0-9A-Z]/
    for (const char of zip) {
        if (!regexExp.test(char)) {
            return i18n.t("common:validation.incorrect_value")
        }
    }
}

export const normalizeLength =
    (maxLength: number, allowManyZeros?: boolean): ((value: string) => string) =>
    (value: string) => {
        if (!allowManyZeros && value == "00") return "0"
        if (value.length > maxLength) {
            return value.slice(0, maxLength)
        }
        return value
    }
